import { MealPicker } from '../components/MealPicker';
import { FoodBrandInput } from '../components/FoodBrandInput';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  parseNumericInput,
  formatNumber,
  formatDecimal,
  formatShortDate,
  type Food,
  type FoodEntry,
  type Meal,
} from '@jungles/shared';
import { api } from '../lib/api';
import { displayFoodBrand, foodPortionGrams } from '../lib/food-search';
import { useApi } from '../lib/hooks';
import { useToast } from '../app/toast';
import { hapticSuccess } from '../lib/telegram';
import { BackLink, Header, Loader, Screen, TabBar } from '../components/Chrome';

/**
 * N1 · Продукт вручную и N3 · Проверка значений.
 * Явное «Добавить»/«Сохранить» подтверждает показанные значения и порцию.
 */

export interface ScanPrefill {
  name: string;
  foodId?: string;
  brand: string | null;
  grams: number;
  basis: 'per100' | 'perServing';
  kcal: number | null;
  protein: number | null;
  fat: number | null;
  carbs: number | null;
  barcode?: string | null;
  source: 'barcode' | 'ocr';
  barcodeOrigin?: 'lookup' | 'manual' | 'ocr';
  notes?: string | null;
  nutritionBasis?: Food['nutritionBasis'];
}

type Basis = 'per100' | 'perServing';
type NutrientKey = 'kcal' | 'protein' | 'fat' | 'carbs';
interface NutrientInput {
  text: string;
  value: number;
  basis: Basis;
}
const NUTRIENTS: NutrientKey[] = ['kcal', 'protein', 'fat', 'carbs'];

function nutrient(value: number | null, basis: Basis = 'per100'): NutrientInput {
  return { text: value === null || !Number.isFinite(value) ? '' : String(Number(value.toFixed(1))), value: value ?? NaN, basis };
}

function portionNumber(value: number): string {
  return Number.isFinite(value) ? formatNumber(value, 1) : '—';
}

interface FormState {
  name: string;
  brand: string;
  grams: string;
  standardGrams: string;
  basis: 'per100' | 'perServing';
  kcal: NutrientInput;
  protein: NutrientInput;
  fat: NutrientInput;
  carbs: NutrientInput;
}

const EMPTY: FormState = {
  name: '',
  brand: '',
  grams: '150',
  standardGrams: '',
  basis: 'per100',
  kcal: nutrient(null),
  protein: nutrient(null),
  fat: nutrient(null),
  carbs: nutrient(null),
};

function num(value: string): number {
  return parseNumericInput(value);
}

// Округлённый текст никогда не участвует в расчётах. Переключения считаем
// от последнего ввода, а не от результата предыдущего переключения.
function portionDenominator(form: FormState): number {
  return form.standardGrams.trim() ? num(form.standardGrams) : num(form.grams);
}

function nutrientValue(form: FormState, key: NutrientKey, unverified = false): number {
  const input = form[key];
  if (unverified || input.basis === form.basis) return input.value;
  return input.value * (form.basis === 'perServing' ? portionDenominator(form) / 100 : 100 / portionDenominator(form));
}

function nutrientText(form: FormState, key: NutrientKey, unverified = false): string {
  return unverified || form[key].basis === form.basis ? form[key].text : nutrient(nutrientValue(form, key, unverified)).text;
}

export function FoodFormScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [meal, setMeal] = useState<Meal>((params.get('meal') ?? 'snack') as Meal);
  const date = params.get('date') ?? '';
  const foodId = params.get('foodId');
  const entryId = params.get('entry');

  const selection = location.state as { prefill?: ScanPrefill; food?: Food; catalogSelection?: boolean; portionGrams?: number | null } | null;
  const prefill = entryId ? null : selection?.prefill ?? null;
  const selectedFood = !entryId && selection?.food?.id === foodId ? selection.food : null;

  const { data: food, loading, error: foodError } = useApi<Food>(
    foodId && !entryId && !selectedFood ? `/api/foods/${encodeURIComponent(foodId)}` : null,
  );

  const { data: entry, loading: entryLoading, error: entryError } = useApi<
    FoodEntry & Pick<Food, 'kcal100' | 'p100' | 'f100' | 'c100'>
  >(entryId ? `/api/nutrition/entries/${encodeURIComponent(entryId)}` : null);

  const [form, setForm] = useState<FormState>(() => {
    if (prefill) {
      return {
        standardGrams: '',
        name: prefill.name,
        brand: prefill.brand ?? '',
        grams: selection?.catalogSelection ? String(prefill.grams).replace('.', ',') : String(prefill.grams),
        basis: prefill.basis,
        kcal: nutrient(prefill.kcal, prefill.basis),
        protein: nutrient(prefill.protein, prefill.basis),
        fat: nutrient(prefill.fat, prefill.basis),
        carbs: nutrient(prefill.carbs, prefill.basis),
      };
    }
    return EMPTY;
  });

  const [barcode, setBarcode] = useState(prefill?.barcode ?? selectedFood?.barcode ?? params.get('barcode') ?? '');
  const sourceBasis = prefill?.nutritionBasis ?? selectedFood?.nutritionBasis ?? food?.nutritionBasis;
  const [unit, setUnit] = useState<'' | 'g' | 'ml'>(() => sourceBasis === 'off_100g_physical_basis_unverified' ? '' : sourceBasis === 'per100ml' ? 'ml' : 'g');
  const basisUnverified = unit !== 'g';
  const changeUnit = (next: '' | 'g' | 'ml') => {
    if (next === unit) return;
    if (unit === 'ml' || unit === 'g' && next === 'ml') setForm(current => ({...current, basis:'per100', ...Object.fromEntries(NUTRIENTS.map(key => [key,nutrient(null)]))}));
    setUnit(next);
  };
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Каталог задаёт исходные значения; правки КБЖУ относятся только к записи.
  const catalogFood = useMemo(() => {
    if (!foodId || entryId) return null;
    return selectedFood ?? (food?.id === foodId ? food : null);
  }, [food, foodId, selectedFood, entryId]);

  const picked = catalogFood;
  const gramPicked = picked && (!picked.nutritionBasis || picked.nutritionBasis === 'per100g') ? picked : null;
  const portionGrams = picked ? foodPortionGrams(picked)
    : !entryId && selection?.catalogSelection ? foodPortionGrams({ defaultGrams: selection.portionGrams ?? undefined }) : null;

  useEffect(() => {
    if (!picked || entryId) return;
    setBarcode(params.get('barcode') || picked.barcode || '');
    setUnit(picked.nutritionBasis === 'per100ml' ? 'ml' : picked.nutritionBasis === 'off_100g_physical_basis_unverified' ? '' : 'g');
    setForm((current) => ({
      ...current,
      standardGrams: picked.servingGrams == null ? '' : String(picked.servingGrams),
      name: picked.name,
      brand: picked.brand ?? '',
      grams: String(foodPortionGrams(picked) ?? 100).replace('.', ','),
      basis: 'per100',
      kcal: nutrient(picked.kcal100),
      protein: nutrient(picked.p100),
      fat: nutrient(picked.f100),
      carbs: nutrient(picked.c100),
    }));
  }, [picked, entryId]);

  useEffect(() => {
    if (!entry || entry.id !== entryId) return;
    setMeal(entry.meal);
    setForm({
      standardGrams: '',
      name: entry.name, brand: entry.brand ?? '', grams: String(entry.grams),
      basis: 'per100', kcal: nutrient(entry.kcal100), protein: nutrient(entry.p100),
      fat: nutrient(entry.f100), carbs: nutrient(entry.c100),
    });
  }, [entry, entryId]);

  const update = (patch: Partial<FormState>) => {
    if (basisUnverified && patch.basis === 'perServing') return;
    setForm((current) => {
      const next = { ...current, ...patch };
      if (!basisUnverified && (patch.grams !== undefined || patch.standardGrams !== undefined)) {
        // Вес меняет порцию, но не уже введённые КБЖУ выбранного базиса.
        for (const key of NUTRIENTS) {
          if (current[key].basis !== current.basis) {
            next[key] = { text: nutrientText(current, key), value: nutrientValue(current, key), basis: current.basis };
          }
        }
      }
      return next;
    });
  };

  const updateNutrient = (key: NutrientKey, text: string) => {
    setForm((current) => {
      const input = { text, value: num(text), basis: current.basis };
      return { ...current, [key]: input };
    });
  };

  const grams = num(form.grams);
  const factor = basisUnverified ? NaN : form.basis === 'per100' ? grams / 100 : grams / portionDenominator(form);
  const portionKcal = nutrientValue(form, 'kcal', basisUnverified) * factor;
  // Compare effective per-100 values, retaining exact source values on unchanged roundtrips.
  const snapshot = entryId ? entry : gramPicked;
  const nutrientsEdited = snapshot ? NUTRIENTS.some((key) => {
    const original = { kcal: snapshot.kcal100, protein: snapshot.p100, fat: snapshot.f100, carbs: snapshot.c100 }[key];
    const value = nutrientValue(form, key, basisUnverified) * (form.basis === 'perServing' ? 100 / portionDenominator(form) : 1);
    return !Number.isFinite(value) || Math.abs(value - original) > Number.EPSILON * Math.max(1, Math.abs(value), Math.abs(original)) * 8;
  }) : true;

  const validGrams = Number.isFinite(grams) && grams >= 1 && grams <= 5000;
  const validStandard = !form.standardGrams.trim() || Number.isFinite(num(form.standardGrams)) && num(form.standardGrams) > 0 && num(form.standardGrams) <= 5000;
  const validNutrients = !basisUnverified && NUTRIENTS.every((key) => {
    const value = nutrientValue(form, key, basisUnverified);
    const limit = key === 'kcal' ? 10000 : 1000;
    const per100 = value * (form.basis === 'perServing' ? 100 / portionDenominator(form) : 1);
    return Number.isFinite(value) && value >= 0 && value <= limit && per100 <= limit;
  });
  const canSubmit =
    !basisUnverified &&
    (!foodId || Boolean(picked) && !loading && !foodError) &&
    (!entryId || (entry?.id === entryId && !entryLoading && !entryError)) &&
    form.name.trim().length > 0 &&
    validGrams && validStandard &&
    (!barcode || /^\d{8,14}$/.test(barcode)) &&
    validNutrients && form.name.trim().length <= 120 && form.brand.trim().length <= 80;

  const validationError = attempted && !canSubmit ? basisUnverified ? 'Выберите граммы и введите КБЖУ на 100 г' : !validNutrients ? 'Заполните все поля КБЖУ' : 'Проверьте поля продукта и вес' : null;

  const submit = async () => {
    if (busy) return;
    setAttempted(true);
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const nutrientOverride = {
        basis: form.basis,
        ...(form.standardGrams.trim() ? { servingGrams: num(form.standardGrams) } : {}),
        kcal: nutrientValue(form, 'kcal'), protein: nutrientValue(form, 'protein'),
        fat: nutrientValue(form, 'fat'), carbs: nutrientValue(form, 'carbs'),
      };
      if (entryId) {
        await api.patch(`/api/nutrition/entries/${entryId}`, { grams, meal, ...(nutrientsEdited ? { nutrientOverride } : {}) });
      } else if (picked || prefill?.foodId) {
        const selectedId = picked?.id ?? prefill!.foodId!;
        await api.post('/api/nutrition/entries', { date: date || undefined, meal, grams, foodId: selectedId,
          ...(nutrientsEdited || !gramPicked || selectedId.startsWith('off:') ? { nutrientOverride } : {}),
          ...(selectedId.startsWith('off:') && prefill ? { food: { name: form.name.trim(), brand: form.brand.trim() || null, ...nutrientOverride } } : {}) });
      } else {
        await api.post('/api/nutrition/entries', {
          date: date || undefined,
          meal,
          grams,
          publicContribution: true,
          food: {
            name: form.name.trim(),
            brand: form.brand.trim() || null,
            barcode: barcode || null,
            kcal: nutrientValue(form, 'kcal', basisUnverified),
            protein: nutrientValue(form, 'protein', basisUnverified),
            fat: nutrientValue(form, 'fat', basisUnverified),
            carbs: nutrientValue(form, 'carbs', basisUnverified),
            basis: form.basis,
            servingGrams: form.standardGrams.trim() ? num(form.standardGrams) : null,
          },
        });
      }
      hapticSuccess();
      toast(entryId ? 'Запись сохранена' : 'Добавили в дневник');
      navigate(`/nutrition?date=${entry?.date ?? date}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось добавить');
      setBusy(false);
    }
  };

  const removeEntry = async () => {
    if (!entryId || entry?.id !== entryId || entryLoading || entryError || busy) return;
    if (!window.confirm(`Удалить «${entry.name}» из дневника?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.del(`/api/nutrition/entries/${encodeURIComponent(entryId)}`);
      toast('Запись удалена');
      navigate(`/nutrition?date=${entry.date}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить');
      setBusy(false);
    }
  };

  if ((loading && foodId) || (entryLoading && entryId)) {
    return (
      <Screen tabs>
        <Header />
        <Loader />
        <TabBar />
      </Screen>
    );
  }

  const readOnlyIdentity = Boolean(picked) || Boolean(entryId) || Boolean(selection?.catalogSelection && prefill);

  return (
    <Screen tabs>
      <Header />
      <BackLink to={-1} label="Назад" />
      <h1>
        {entryId ? 'Изменить запись' : prefill ? 'Проверьте значения' : 'Добавить продукт'}
      </h1>
      <span className="ctx food-context">
        <MealPicker value={meal} onChange={setMeal} /> · {entry?.date || date ? formatShortDate(entry?.date ?? date) : 'сегодня'}
      </span>

      <section className="card">
        <h2>Продукт и порция</h2>

        {readOnlyIdentity ? (
          <dl className="food-identity">
            <dt>Название</dt>
            <dd>{form.name}</dd>
            <dt>Бренд</dt>
            <dd>{displayFoodBrand(form.brand) || 'Не указан'}</dd>
            {portionGrams !== null ? <>
              <dt>Порция</dt>
              <dd>{formatDecimal(portionGrams)} г</dd>
            </> : null}
          </dl>
        ) : (
          <>
            <label className="fl">
              Название
              <input
                className="f"
                value={form.name}
                onChange={(event) => update({ name: event.target.value })}
                placeholder="Например, творог 5%"
              />
            </label>
            <FoodBrandInput value={form.brand} onChange={brand => update({brand})} />
          </>
        )}

        {barcode && (prefill?.barcodeOrigin === 'lookup' || picked) ? <p className="nt">Штрихкод: <span className="code food-barcode">{barcode}</span></p> : prefill?.barcode || prefill?.source === 'ocr' ? (
          <label className="fl">Штрихкод<input className="f" inputMode="numeric" value={barcode} onChange={e => setBarcode(e.target.value)} /></label>
        ) : null}

        <label className="fl">
          Вес, г
          <input
            className="f"
            type="text"
            inputMode="decimal"
            value={form.grams}
            onChange={(event) => update({ grams: event.target.value })}
          />
        </label>

        {!picked && !entryId && !selection?.catalogSelection ? <label className="fl">
          Стандартная порция, г <span className="mu">· необязательно</span>
          <input className="f" aria-label="Стандартная порция, г" inputMode="decimal" value={form.standardGrams} onChange={event => update({ standardGrams: event.target.value })} />
          <span className="mu">Вес обычной порции для следующего выбора. Съеденный вес вводится отдельно.</span>
        </label> : null}

        <>
            {picked || prefill?.foodId || entryId ? <p className="nt">Изменения КБЖУ сохраняются только в этой записи дневника.</p> : null}
            {sourceBasis && sourceBasis !== 'per100g' ? <label className="fl food-unit">Единица КБЖУ
              <select aria-label="Единица КБЖУ" className="f" value={unit} onChange={e => changeUnit(e.target.value as '' | 'g' | 'ml')}>
                <option value="" disabled>Выберите единицу</option><option value="g">На 100 г</option><option value="ml">На 100 мл</option>
              </select>
              {unit === 'ml' ? <span className="mu">Для записи веса введите КБЖУ на 100 г</span> : null}
            </label> : null}
            <div className="perrow">
              <span className="seglbl" id="basis-label">
                КБЖУ указаны
              </span>
              <div className="seg" role="radiogroup" aria-labelledby="basis-label">
                <button
                  type="button"
                  role="radio"
                  aria-checked={form.basis === 'per100'}
                  disabled={!validGrams}
                  className={form.basis === 'per100' ? 'on' : undefined}
                  onClick={() => update({ basis: 'per100' })}
                >
                  {unit === 'ml' ? 'на 100 мл' : unit === '' ? 'на 100' : 'на 100 г'}
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={form.basis === 'perServing'}
                  disabled={!validGrams || basisUnverified}
                  className={form.basis === 'perServing' ? 'on' : undefined}
                  onClick={() => update({ basis: 'perServing' })}
                >
                  на порцию
                </button>
              </div>
            </div>

            {form.basis === 'perServing' ? <p className="nt">КБЖУ на порцию {portionNumber(portionDenominator(form))} г{form.standardGrams.trim() ? ' · стандартная порция' : ' · по введённому весу'}</p> : null}
            <div className="fields">
              <label className="fl">
                Калории, ккал
                <input
                  className="f"
                  type="text"
                  inputMode="decimal"
                  aria-label="Калории, ккал"
                  aria-invalid={attempted && !Number.isFinite(nutrientValue(form, 'kcal', basisUnverified)) || undefined}
                  aria-describedby={attempted && !Number.isFinite(nutrientValue(form, 'kcal', basisUnverified)) ? 'kcal-error' : undefined}
                  value={nutrientText(form, 'kcal', basisUnverified)}
                  onChange={(event) => updateNutrient('kcal', event.target.value)}
                />
                {attempted && !Number.isFinite(nutrientValue(form, 'kcal', basisUnverified)) ? <span className="err" id="kcal-error">Введите значение</span> : null}
              </label>
              <label className="fl">
                Белки, г
                <input
                  className="f"
                  type="text"
                  inputMode="decimal"
                  aria-label="Белки, г"
                  aria-invalid={attempted && !Number.isFinite(nutrientValue(form, 'protein', basisUnverified)) || undefined}
                  aria-describedby={attempted && !Number.isFinite(nutrientValue(form, 'protein', basisUnverified)) ? 'protein-error' : undefined}
                  value={nutrientText(form, 'protein', basisUnverified)}
                  onChange={(event) => updateNutrient('protein', event.target.value)}
                />
                {attempted && !Number.isFinite(nutrientValue(form, 'protein', basisUnverified)) ? <span className="err" id="protein-error">Введите значение</span> : null}
              </label>
              <label className="fl">
                Жиры, г
                <input
                  className="f"
                  type="text"
                  inputMode="decimal"
                  aria-label="Жиры, г"
                  aria-invalid={attempted && !Number.isFinite(nutrientValue(form, 'fat', basisUnverified)) || undefined}
                  aria-describedby={attempted && !Number.isFinite(nutrientValue(form, 'fat', basisUnverified)) ? 'fat-error' : undefined}
                  value={nutrientText(form, 'fat', basisUnverified)}
                  onChange={(event) => updateNutrient('fat', event.target.value)}
                />
                {attempted && !Number.isFinite(nutrientValue(form, 'fat', basisUnverified)) ? <span className="err" id="fat-error">Введите значение</span> : null}
              </label>
              <label className="fl">
                Углеводы, г
                <input
                  className="f"
                  type="text"
                  inputMode="decimal"
                  aria-label="Углеводы, г"
                  aria-invalid={attempted && !Number.isFinite(nutrientValue(form, 'carbs', basisUnverified)) || undefined}
                  aria-describedby={attempted && !Number.isFinite(nutrientValue(form, 'carbs', basisUnverified)) ? 'carbs-error' : undefined}
                  value={nutrientText(form, 'carbs', basisUnverified)}
                  onChange={(event) => updateNutrient('carbs', event.target.value)}
                />
                {attempted && !Number.isFinite(nutrientValue(form, 'carbs', basisUnverified)) ? <span className="err" id="carbs-error">Введите значение</span> : null}
              </label>
            </div>
        </>

        <div className="ins">
          {'Съедено ' + portionNumber(grams) + ' г: ' + portionNumber(portionKcal)} ккал · Белки {portionNumber(nutrientValue(form, 'protein', basisUnverified) * factor)} г
          {' · '}Жиры {portionNumber(nutrientValue(form, 'fat', basisUnverified) * factor)} г
          {' · '}Углеводы {portionNumber(nutrientValue(form, 'carbs', basisUnverified) * factor)} г
        </div>
        {entryError || foodError || error || validationError ? <p className="err" role="alert">{entryError ?? foodError ?? error ?? validationError}</p> : null}

        <div className="acts">
          <button
            className="b p big"
            type="button"
            onClick={() => void submit()}
            disabled={busy || !validGrams || !form.name.trim() || Boolean(barcode && !/^\d{8,14}$/.test(barcode))}
          >
            {entryId ? 'Сохранить' : 'Добавить'}
          </button>
        </div>
        {entryId ? (
          <button className="b d" type="button" style={{ marginTop: 12 }}
            disabled={busy || entryLoading || Boolean(entryError) || entry?.id !== entryId}
            onClick={() => void removeEntry()}>
            Удалить запись
          </button>
        ) : null}
      </section>

      <TabBar />
    </Screen>
  );
}
